import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppShell } from "@/components/looseend/AppShell";

import { InspectorProvider } from "@/components/looseend/inspector-context";
import { LooseEndBrandLockup } from "@/components/looseend/BrandLockup";
import { LooseEndLoadingIndicator } from "@/components/looseend/LooseEndLoadingIndicator";
import { hydrate, store, useAppState } from "@/lib/looseend/store";
import { analytics } from "@/lib/analytics";
import { ensureGuestSession, useAuthSession } from "@/lib/auth/session";
import { useAccountBootstrap } from "@/lib/account/useAccountBootstrap";
import { useGuestClaim } from "@/lib/auth/guest/useGuestClaim";
import { AccountGate, AccountGateScreen } from "@/components/looseend/AccountGateScreen";

/**
 * Guest-first access. Every /app route is public: a visitor without a session
 * receives an anonymous GUEST backend identity here, on first load, and then
 * passes through exactly the same account gate as a signed-in user. The
 * session lives in the browser, so the subtree opts out of SSR.
 *
 * There is no sign-in wall. Sign-in is offered from within the shell as the way
 * to save history across sessions and devices.
 */
export const Route = createFileRoute("/app")({
  ssr: false,
  component: AppLayout,
});

type GuestPhase = "idle" | "pending" | "failed";

/** Canonical browser IANA zone, or null when the runtime cannot report one. */
function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.includes("/") ? zone : null;
  } catch {
    return null;
  }
}

function AppLayout() {
  const state = useAppState();
  const session = useAuthSession();
  const [guest, setGuest] = useState<GuestPhase>("idle");
  const guestAttempt = useRef(0);

  useEffect(() => {
    hydrate();
  }, []);

  useEffect(() => {
    analytics.init();
    analytics.track("app_opened", { interface_surface: "app_shell" });
  }, []);

  // No session at all → establish a guest identity. The visitor never sees this
  // as a login event; from their side, they simply start chatting.
  useEffect(() => {
    if (session.status !== "unauthenticated" || guest !== "idle") return;
    const attempt = ++guestAttempt.current;
    setGuest("pending");
    void ensureGuestSession().then((ok) => {
      if (attempt !== guestAttempt.current) return;
      // Success surfaces through onAuthStateChange → session becomes authenticated.
      setGuest(ok ? "idle" : "failed");
    });
  }, [session.status, guest]);

  const retryGuest = useCallback(() => setGuest("idle"), []);

  // Mirror the Cloud identity (immutable id) into the UI store.
  useEffect(() => {
    if (!state.hydrated || session.status === "loading") return;
    store.setUser(session.user);
  }, [state.hydrated, session.status, session.user]);

  // Reconcile the backend account graph for the verified identity — guest or
  // account alike. This is a REQUIRED gate: the subtree renders only once it
  // succeeds. The subject is derived server-side from the bearer token.
  //
  // WP-PT — the browser's IANA time zone is offered as the creation-time
  // default (an existing, validated bootstrap input). Real AI resolves dates in
  // the STORED zone, so a new account should start from the user's real one
  // rather than the server default. It is ignored for an existing account.
  const gate = useAccountBootstrap({
    userId: session.status === "authenticated" && session.user ? session.user.id : null,
    timeZone: browserTimeZone(),
  });

  // After a guest signs in with a different identity, bring the guest's hosted
  // conversations into the account (server-authoritative, idempotent).
  useGuestClaim(session.user, gate.phase === "ready");

  if (guest === "failed") {
    return <AccountGateScreen phase="unavailable" onRetry={retryGuest} />;
  }

  if (!state.hydrated || session.status !== "authenticated" || !state.user) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background px-6">
        <div className="flex flex-col items-center text-center">
          <LooseEndBrandLockup variant="full" className="[&_p]:text-center" />
          <LooseEndLoadingIndicator
            size={56}
            className="mt-8 animate-fade-in"
            label="Starting the Personal Execution Engine"
          />

          <p className="sr-only" role="status">
            Loading your workspace
          </p>
        </div>
      </div>
    );
  }

  return (
    <AccountGate phase={gate.phase} onRetry={gate.retry}>
      <TooltipProvider delayDuration={200}>
        <InspectorProvider>
          <AppShell>
            <Outlet />
          </AppShell>
        </InspectorProvider>
      </TooltipProvider>
    </AccountGate>
  );
}
